// Auth Callback - Handles OAuth redirect from Google/Apple/Facebook
// Supabase puts tokens in URL hash; client auto-detects on getSession()

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabaseAuth } from '../../services/supabaseAuth';
import { userAuth } from '../../services/userAuth';
import { trackOnboardingEvent, upsertUserProfile } from '../../services/supabaseService';
import { funnelEventTracker } from '../../services/funnelEventTracker';
import { logger } from '../../utils/logger';
import { getTranslation } from '../../utils/translationHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { BrandedLoading } from '../BrandedLoading';

const sanitizeInternalReturnPath = (value: string | null): string | null => {
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null;
  return value;
};

const PENDING_PLAN_KEY = 'subscription.pendingPlan';
const PENDING_PLAN_FALLBACK_KEY = 'subscription.pendingPlan.local';

const hasPendingSubscriptionPlan = (): boolean => {
  const pendingPlan =
    sessionStorage.getItem(PENDING_PLAN_KEY) ||
    localStorage.getItem(PENDING_PLAN_FALLBACK_KEY);
  return pendingPlan === 'weekly' || pendingPlan === 'monthly' || pendingPlan === 'lifetime';
};

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t_nested } = useLanguage();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Supabase client auto-detects session from URL hash (#access_token=...)
        const supabaseUser = await supabaseAuth.getCurrentUser();

        if (!supabaseUser) {
          // No session - might still be processing; retry once after short delay
          await new Promise((r) => setTimeout(r, 500));
          const retryUser = await supabaseAuth.getCurrentUser();
          if (!retryUser) {
            setStatus('error');
            setErrorMessage(
              getTranslation(t_nested, 'auth.signInCouldNotComplete', 'Sign-in could not be completed. Please try again.')
            );
            return;
          }
        }

        const user = supabaseUser
          ? {
              id: supabaseUser.id,
              email: supabaseUser.email || '',
              name: supabaseUser.name || 'User',
              createdAt: new Date().toISOString(),
              isGuest: false
            }
          : null;

        if (user) {
          localStorage.setItem('userAuthenticated', 'true');
          localStorage.setItem('username', user.name);
          localStorage.setItem('userEmail', user.email);
          localStorage.setItem('userId', user.id);
          localStorage.setItem('currentUser', JSON.stringify(user));
          userAuth.refreshUser();

          const pendingExamDate = localStorage.getItem('EXAM_DATE_PENDING');
          const pendingGoal = localStorage.getItem('selectedGoal');
          if (pendingExamDate || pendingGoal) {
            upsertUserProfile(user.id, {
              exam_date: pendingExamDate || undefined,
              user_goal: pendingGoal || undefined
            }).catch(() => {});
            if (pendingExamDate) localStorage.removeItem('EXAM_DATE_PENDING');
            if (pendingGoal) localStorage.removeItem('selectedGoal');
          }

          // Track signup in Supabase (onboarding funnel)
          trackOnboardingEvent(user.id, 'signup', { provider: supabaseUser.provider }).catch(() => {});

          // Track funnel: signup gate passed (from AI exam 1st gate)
          if (localStorage.getItem('aiExamSignupGate') === 'true') {
            funnelEventTracker.signupGatePassed();
            localStorage.removeItem('aiExamSignupGate');
          }

          // Dispatch for components listening (e.g. PracticeTest)
          window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user } }));

          setStatus('success');

          // Redirect: check for stored return URL or nextTestId
          // Historic keys: most of the app uses `returnToResults`; keep `authReturnToResults` for backwards compatibility.
          const returnToResults =
            localStorage.getItem('returnToResults') || localStorage.getItem('authReturnToResults');
          const pendingReturnTestId = localStorage.getItem('pendingReturnTestId');
          const nextTestId = searchParams.get('nextTestId') || localStorage.getItem('pendingNextTestId');
          const authReturnTo = sanitizeInternalReturnPath(localStorage.getItem('authReturnTo'));
          const fallbackReturnTo = hasPendingSubscriptionPlan() ? '/subscription' : null;
          const resolvedReturnTo = authReturnTo || fallbackReturnTo;
          localStorage.removeItem('returnToResults');
          localStorage.removeItem('authReturnToResults');
          localStorage.removeItem('pendingReturnTestId');
          localStorage.removeItem('pendingNextTestId');
          localStorage.removeItem('authReturnTo');

          if (returnToResults === 'true' && pendingReturnTestId) {
            navigate(`/practice/${pendingReturnTestId}?results=true`, { replace: true });
          } else if (returnToResults === 'true' && nextTestId) {
            // Fallback: return to a specific practice route if we only have nextTestId.
            navigate(`/practice/${nextTestId}?results=true`, { replace: true });
          } else if (resolvedReturnTo) {
            navigate(resolvedReturnTo, { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          setStatus('error');
          setErrorMessage(
            getTranslation(t_nested, 'auth.signInCouldNotCompleteShort', 'Sign-in could not be completed.')
          );
        }
      } catch (error) {
        logger.error('Auth callback error:', error);
        setStatus('error');
        setErrorMessage(
          getTranslation(t_nested, 'auth.signInUnexpectedError', 'An error occurred during sign-in. Please try again.')
        );
      }
    };

    handleCallback();
  }, [navigate, searchParams, t_nested]);

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: '2rem',
        fontFamily: 'Inter, sans-serif'
      }}
      >
        <BrandedLoading context="general" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: '2rem',
        fontFamily: 'Inter, sans-serif',
        textAlign: 'center'
      }}>
        <p style={{ color: '#DC2626', marginBottom: '1rem' }}>{errorMessage}</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--color-blue)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return null;
};
